import { createContext, useContext, useEffect, useState } from 'react';

import type { ContainerComponent } from '../../common/types/Common';
import { AnonymousFeatureFlagsProvider } from '../../feature-flags/providers/FeatureFlagsProvider';
import { identifyVisitor } from '../../logger/Logger';
import { visitorId as getVisitorId } from '../utils/visitorId';
import { useUser } from './UserProvider';

const VisitorContext = createContext<string | null>(null);

/** The provider id when mounted under VisitorProvider, otherwise null. */
export const useOptionalVisitorId = (): string | null =>
    useContext(VisitorContext);

export const useVisitorId = (): string => {
    const id = useOptionalVisitorId();
    if (id == null) {
        throw new Error('useVisitorId must be used within VisitorProvider');
    }
    return id;
};

export const VisitorProvider: ContainerComponent = ({ children }) => {
    const [visitorIdValue] = useState(() => getVisitorId());
    const { user, loading } = useUser();

    useEffect(() => {
        if (loading || user != null) {
            return;
        }

        identifyVisitor(visitorIdValue);
    }, [visitorIdValue, user, loading]);

    return (
        <VisitorContext.Provider value={visitorIdValue}>
            <AnonymousFeatureFlagsProvider visitorId={visitorIdValue}>
                {children}
            </AnonymousFeatureFlagsProvider>
        </VisitorContext.Provider>
    );
};
